import React from 'react';
import { Link } from 'react-router-dom';
import { TOUR_STOPS, IMAGES } from '../lib/content';
import { MapPin } from 'lucide-react';
import TourSubscribe from '../components/TourSubscribe';

const STATUS_LABEL = {
  home: 'Home base',
  'taking-bookings': 'Taking bookings',
  limited: 'Limited availability',
  'pre-booking': 'Pre-bookings open',
  fmty: 'Fly Me To You',
};

export default function Tour() {
  return (
    <div>
      <section className="relative">
        <div className="absolute inset-0">
          <img
            src={IMAGES.parallax}
            alt=""
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 30%', filter: 'brightness(0.7)' }}
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 md:pt-36 pb-28 md:pb-40 text-zb-bg">
          <div className="font-label text-zb-bg/85 mb-6">Tour</div>
          <h1 className="font-serif text-6xl md:text-8xl tracking-tight leading-[0.9]">
            Where I&rsquo;m <span className="italic">wandering.</span>
          </h1>
          <p className="mt-8 max-w-xl text-zb-bg/90 text-lg leading-relaxed">
            A calendar of upcoming cities. Pre-bookings secure the most sought-after
            nights. Send me a note and I&rsquo;ll make sure we meet.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <ul>
          {TOUR_STOPS.map((s, i) => (
            <li
              key={s.city + i}
              data-testid={`tour-stop-${i}`}
              className="border-b border-zb-border zb-fade-up"
              style={{ animationDelay: `${i * 0.06}s` }}
            >
              <Link
                to={`/contact?city=${encodeURIComponent(s.city)}`}
                data-testid={`tour-stop-cta-${i}`}
                className="group grid grid-cols-12 gap-4 py-8 items-center transition-colors hover:bg-zb-bg2 -mx-4 px-4 md:-mx-6 md:px-6"
              >
                <div className="col-span-1 text-zb-accent">
                  <MapPin size={18} />
                </div>
                <div className="col-span-11 md:col-span-5">
                  <div className="font-serif text-3xl md:text-4xl text-zb-ink group-hover:text-zb-brand transition-colors">
                    {s.city}
                  </div>
                </div>
                <div className="col-span-7 md:col-span-4 text-zb-ink2 text-base">
                  {s.dates}
                </div>
                <div className="col-span-5 md:col-span-2 text-right">
                  <span
                    className={`inline-block font-label text-[10.5px] px-4 py-2 rounded-full border transition-colors ${
                      s.status === 'home'
                        ? 'border-zb-ink text-zb-ink group-hover:bg-zb-ink group-hover:text-zb-bg'
                        : s.status === 'limited'
                        ? 'border-zb-brand text-zb-brand group-hover:bg-zb-brand group-hover:text-zb-bg'
                        : s.status === 'pre-booking'
                        ? 'border-zb-accent text-zb-accent group-hover:bg-zb-accent group-hover:text-zb-bg'
                        : s.status === 'fmty'
                        ? 'border-zb-ink2 text-zb-ink2'
                        : 'border-zb-brand bg-zb-brand text-zb-bg group-hover:bg-zb-brandHover'
                    }`}
                  >
                    {STATUS_LABEL[s.status]} →
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>

        <div className="mt-14 text-center">
          <p className="font-serif italic text-2xl md:text-3xl text-zb-ink max-w-2xl mx-auto">
            &ldquo;Travel is my favourite love language.&rdquo;
          </p>
          <Link
            to="/contact"
            data-testid="tour-cta-book"
            className="inline-block mt-10 rounded-full bg-zb-brand hover:bg-zb-brandHover text-zb-bg px-10 py-4 text-xs tracking-[0.22em] uppercase transition-colors"
          >
            Secure a tour booking
          </Link>
        </div>
      </section>

      {/* DON'T SEE YOUR CITY? */}
      <section className="bg-zb-bg2 border-t border-zb-border">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-14 md:py-20">
          <div className="text-center mb-14">
            <div className="font-label text-zb-accent mb-5">Your city isn&rsquo;t on the list?</div>
            <h2 className="font-serif text-4xl md:text-6xl text-zb-ink leading-tight max-w-3xl mx-auto">
              Let&rsquo;s <span className="italic text-zb-brand">make it happen.</span>
            </h2>
            <p className="mt-6 text-zb-ink2 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              My tour schedule is not always set in stone. If you have a
              tantalising offer, a restaurant you&rsquo;ve been saving, a
              hotel with a view, a weekend you&rsquo;d love to disappear into,
              I&rsquo;d love to hear it. If you don&rsquo;t ask, you&rsquo;ll
              never know.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            <div
              data-testid="tour-path-subscribe"
              className="bg-zb-bg border border-zb-border p-8 md:p-10 flex flex-col"
            >
              <div className="font-label text-zb-accent mb-4">Path one</div>
              <h3 className="font-serif text-3xl md:text-4xl text-zb-ink leading-tight">
                Tempt me.
              </h3>
              <p className="mt-5 text-zb-ink2 text-base leading-relaxed flex-1">
                Tell me where you are. Your interest helps me choose the next
                cities I visit. Add your email below and you&rsquo;ll be the
                first to know when I announce dates for your part of the world.
              </p>
              <a
                href="#tour-subscribe"
                data-testid="tour-path-subscribe-link"
                className="inline-block mt-8 rounded-full border border-zb-border hover:border-zb-brand text-zb-ink hover:text-zb-brand px-7 py-3 text-xs tracking-[0.22em] uppercase transition-colors self-start"
              >
                Join the list ↓
              </a>
            </div>

            <div
              data-testid="tour-path-fmty"
              className="bg-zb-ink text-zb-bg p-8 md:p-10 flex flex-col"
            >
              <div className="font-label text-zb-bg/70 mb-4">Path two</div>
              <h3 className="font-serif text-3xl md:text-4xl leading-tight">
                Take the reins.
              </h3>
              <p className="mt-5 text-zb-bg/85 text-base leading-relaxed flex-1">
                Make your dreams come true, and fly me to you. An overnight
                minimum plus flights brings me to your door, on your terms,
                on your timeline. The most romantic way to bypass a calendar.
              </p>
              <Link
                to="/contact"
                data-testid="tour-path-fmty-link"
                className="inline-block mt-8 rounded-full bg-zb-bg hover:bg-zb-brand text-zb-ink hover:text-zb-bg px-7 py-3 text-xs tracking-[0.22em] uppercase transition-colors self-start"
              >
                Fly me to you →
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section id="tour-subscribe" className="bg-zb-bg border-t border-zb-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
          <TourSubscribe />
        </div>
      </section>
    </div>
  );
}
